import React from 'react';
import { useSirenService } from '@europrocurement/l2d-domain';
import { type customItemJsonProps } from '../customItemRenderer';
import { useSirenRneContext } from './SirenRneContext';

const SirenRne: React.FunctionComponent<customItemJsonProps> = function ({ formContext, props }) {
    formContext.watch(props.name);

    const { sirenApi } = useSirenService();
    const { currentSousClient, dossier } = useSirenRneContext();

    // console.log('[SirenRne] monté — currentSousClient:', currentSousClient, '| dossier:', dossier);

    const [siren, setSiren] = React.useState<string>('');
    const [lastSiren, setLastSiren] = React.useState<string>('');

    React.useEffect(() => {
        // console.log('[SYNC] currentSousClient:', currentSousClient, '| dossier:', dossier);

        if (currentSousClient?.siren && currentSousClient.siren !== '') {
            // console.log('[SYNC] → setSiren depuis currentSousClient:', currentSousClient.siren);
            setSiren(currentSousClient.siren);
            return;
        }
        if (dossier?.societeSiren) {
            // console.log('[SYNC] → setSiren depuis dossier.societeSiren:', dossier.societeSiren);
            setSiren(dossier.societeSiren);
            return;
        }
        if (dossier?.clientOpereSiren) {
            // console.log(
            //     '[SYNC] → setSiren depuis dossier.clientOpereSiren:',
            //     dossier.clientOpereSiren,
            // );
            setSiren(dossier.clientOpereSiren);
        }
        // console.warn('[SYNC] → Aucune source de siren disponible');
    }, [currentSousClient, dossier]);

    React.useEffect(() => {
        // console.log('[API] siren:', siren, '| lastSiren:', lastSiren);
        if (siren === '' || lastSiren === siren) return;

        // console.log('[API] → Appel sirenApi pour:', siren);

        sirenApi
            .apiRcsSirenGet({ siren })
            .then((res) => {
                setLastSiren(siren);

                formContext.setValue(
                    'personneMorale.identite.description.currentRneDenomination',
                    res.data?.denomination ?? '',
                );

                const principal = res.data?.etablissements?.principal?.[0];
                const activitePrincipale =
                    principal?.aActivite?.find((a) => a.indicateurPrincipal === true) ??
                    principal?.aActivite?.[0];

                formContext.setValue(
                    'personneMorale.identite.description.objet',
                    activitePrincipale?.descriptionDetaillee ?? '',
                );

                formContext.setValue('custom_l2d.currentRneCapital', res.data?.capital ?? '');

                if (res.data.composition?.structure) {
                    const dirigeants = res.data.composition.structure.map((structureItem) => ({
                        gestionDirigeant: {
                            custom_l2d: {
                                typePersonne: structureItem.denomination
                                    ? { value: 2, label: 'Personne morale' }
                                    : { value: 1, label: 'Personne physique' },
                            },
                            roleEntreprise: {
                                value: structureItem.roleEntreprise,
                                label: structureItem.libelleRoleEntreprise,
                            },
                            individu: {
                                descriptionPersonne: {
                                    nom: structureItem.nom,
                                    nomUsage: structureItem.nomUsage,
                                    prenoms: structureItem.prenoms,
                                },
                            },
                            entreprise: {
                                denomination: structureItem.denomination,
                            },
                        },
                    }));
                    formContext.setValue('gestion_dirigeant', dirigeants);
                }

                if (res.data.etablissements?.autres) {
                    const etablissementsBase = res.data.etablissements.autres.filter(
                        (e) => e.actif === true,
                    );

                    // Pour la fermeture - clé gestionFermetureEtablissement
                    const etablissementsFermeture = etablissementsBase.map((e) => ({
                        gestionFermetureEtablissement: {
                            custom_l2d: { siret: e.siret },
                            personneMorale: {
                                autresEtablissements: {
                                    adresse: {
                                        codePostal: e.address?.codePostal,
                                        commune: e.address?.commune,
                                        voie: e.address?.adresse,
                                    },
                                },
                            },
                        },
                    }));
                    formContext.setValue(
                        'gestion_fermeture_etablissement',
                        etablissementsFermeture,
                    );
                }

                // Pour la modification identité - tous les établissements actifs (principal + autres)
                const etablissementsPrincipal = (res.data.etablissements?.principal ?? [])
                    .filter((e) => e.actif === true)
                    .map((e) => ({
                        gestionEtablissement: {
                            custom_l2d: { siret: e.siret, estPrincipal: true },
                            personneMorale: {
                                autresEtablissements: {
                                    adresse: {
                                        codePostal: e.address?.codePostal,
                                        commune: e.address?.commune,
                                        voie: e.address?.adresse,
                                    },
                                },
                            },
                        },
                    }));

                const etablissementsAutres = (res.data.etablissements?.autres ?? [])
                    .filter((e) => e.actif === true)
                    .map((e) => ({
                        gestionEtablissement: {
                            custom_l2d: { siret: e.siret, estPrincipal: false },
                            personneMorale: {
                                autresEtablissements: {
                                    adresse: {
                                        codePostal: e.address?.codePostal,
                                        commune: e.address?.commune,
                                        voie: e.address?.adresse,
                                    },
                                },
                            },
                        },
                    }));

                formContext.setValue('gestion_modification_identite_etablissement', [
                    ...etablissementsPrincipal,
                    ...etablissementsAutres,
                ]);
            })
            .catch((err) => {
                console.error('[SirenRne] Erreur API:', err);
            });
    }, [siren, sirenApi, formContext, lastSiren]);

    return null;
};

export default SirenRne;
